import Cookies from 'js-cookie'
import * as Sentry from '@sentry/vue'
import { createSessionId } from '../state'

const AUTH_EVENT_KEY = 'auth:event'
const APPLICATION_STATE_KEY = 'applicationState'
const PROJECT_EVENT_KEY = 'project:event'

const getCurrentProjectId = state => state.applicationState.currentProject?.id ?? null

const isSameProject = (firstProjectId, secondProjectId) =>
  firstProjectId !== null && secondProjectId !== null && String(firstProjectId) === String(secondProjectId)

const addCleanupBreadcrumb = (reason, data = {}) => {
  if (typeof Sentry.addBreadcrumb !== 'function') {
    return
  }

  Sentry.addBreadcrumb({
    category: 'auth.cleanup',
    level: 'info',
    message: reason,
    data
  })
}

const clearPersistedApplicationState = (reason, data = {}) => {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(APPLICATION_STATE_KEY)
  }
  addCleanupBreadcrumb(reason, data)
}

const isObjectOrNull = value => value === null || (typeof value === 'object' && !Array.isArray(value))

const normalizeSavedState = (parsedState, accessToken) => {
  if (!parsedState || typeof parsedState !== 'object' || Array.isArray(parsedState)) {
    return null
  }

  if (parsedState.token !== null && typeof parsedState.token !== 'string' && typeof parsedState.token !== 'undefined') {
    return null
  }

  if (!isObjectOrNull(parsedState.associate)) {
    return null
  }

  if (!isObjectOrNull(parsedState.currentProject)) {
    return null
  }

  const normalizedState = {
    ...parsedState,
    internetConnection: {
      checking: false,
      stable: true,
      dismissed: false,
      lastCheckedAt: null
    },
    token: accessToken || null
  }

  if (typeof normalizedState.sessionId !== 'string' || normalizedState.sessionId.length === 0) {
    normalizedState.sessionId = createSessionId()
  }

  return normalizedState
}

const loadStatePlugin = store => {
  const hasAccessToken = Boolean(Cookies.get('access'))
  const savedState = localStorage.getItem(APPLICATION_STATE_KEY)

  if (!hasAccessToken) {
    clearPersistedApplicationState('missing_access_cookie')
  } else if (savedState) {
    try {
      const parsedState = JSON.parse(savedState)
      const normalizedState = normalizeSavedState(parsedState, Cookies.get('access'))

      if (normalizedState) {
        store.commit('setApplicationState', normalizedState)
      } else {
        clearPersistedApplicationState('invalid_schema')
      }
    } catch (_) {
      clearPersistedApplicationState('invalid_json')
    }
  }

  let currentProjectId = getCurrentProjectId(store.state)
  let projectSyncQueue = Promise.resolve()

  store.subscribe((_, state) => {
    const nextProjectId = getCurrentProjectId(state)
    const projectChanged =
      currentProjectId !== null && nextProjectId !== null && !isSameProject(currentProjectId, nextProjectId)

    currentProjectId = nextProjectId

    if (!projectChanged || typeof localStorage === 'undefined') {
      return
    }

    localStorage.setItem(
      PROJECT_EVENT_KEY,
      JSON.stringify({
        type: 'project_changed',
        projectId: nextProjectId,
        eventId: createSessionId(),
        timestamp: Date.now()
      })
    )
  })

  if (typeof window !== 'undefined') {
    window.addEventListener('storage', event => {
      if (![AUTH_EVENT_KEY, PROJECT_EVENT_KEY].includes(event.key) || !event.newValue) {
        return
      }

      try {
        const eventPayload = JSON.parse(event.newValue)

        if (event.key === AUTH_EVENT_KEY && eventPayload?.type === 'logout') {
          if (eventPayload.nextSessionId && eventPayload.nextSessionId === store.state.applicationState.sessionId) {
            return
          }

          store.dispatch('logoutAndReset', {
            reason: 'cross_tab_logout',
            shouldCallSignOutApi: false,
            broadcast: false,
            redirectToAuth: true
          })

          return
        }

        if (
          event.key !== PROJECT_EVENT_KEY ||
          eventPayload?.type !== 'project_changed' ||
          eventPayload.projectId == null
        ) {
          return
        }

        projectSyncQueue = projectSyncQueue
          .catch(() => {})
          .then(async () => {
            if (isSameProject(getCurrentProjectId(store.state), eventPayload.projectId)) {
              return
            }

            await store.dispatch('loadApplicationState')
          })
      } catch (_) {
        addCleanupBreadcrumb(event.key === PROJECT_EVENT_KEY ? 'invalid_project_event' : 'invalid_auth_event')
      }
    })
  }

  const updateApplicationState = async () => {
    try {
      await store.dispatch('loadApplicationState')
    } catch (_) {}
  }

  setInterval(updateApplicationState, 300000)
}

export default loadStatePlugin
